import { logger } from '../utils/log';
import type { EffectId, EffectParams, FaceLandmarks, BackendKind } from './types';

const log = logger('engine');

export interface GpuEngine {
  readonly backend: BackendKind;
  init(canvas: HTMLCanvasElement): Promise<void>;
  setSource(video: HTMLVideoElement): void;
  setEffect(id: EffectId, params?: EffectParams): void;
  setFaceLandmarks(landmarks: FaceLandmarks | null): void;
  /**
   * Register a getter that the engine polls each render frame for the latest
   * face landmarks. Preferred over `setFaceLandmarks` so the React tree
   * doesn't re-render at ~15 Hz when MediaPipe produces a new detection.
   */
  setFaceLandmarksSource(getter: (() => FaceLandmarks | null) | null): void;
  setMirror(mirror: boolean): void;
  /**
   * Override the auto-detected rotation (0=none, 1=90° CW, 2=180°, 3=90° CCW).
   * Pass `null` to fall back to the heuristic in `computeOrientation()`.
   */
  setRotationOverride(rotation: 0 | 1 | 2 | 3 | null): void;
  /** Frames-per-second measured over the last second or so. */
  getLastFps(): number;
  /**
   * Capture the next rendered frame as a JPEG blob. The capture is done
   * synchronously inside the render loop after a fresh draw, so the canvas
   * does NOT need `preserveDrawingBuffer`.
   */
  captureNext(quality?: number): Promise<Blob>;
  start(): void;
  stop(): void;
  destroy(): void;
}

export interface GpuEngineFactoryOptions {
  /** Force a specific backend (test/debug). */
  prefer?: BackendKind;
}

export async function createGpuEngine(
  opts: GpuEngineFactoryOptions = {},
): Promise<GpuEngine> {
  // We default to WebGL2 because Chrome desktop's WebGPU video-import path
  // (importExternalTexture) is currently significantly slower than WebGL2's
  // mature texImage2D upload on this app's render shape. The original spec
  // called for WebGPU-first; we still honor that when explicitly requested
  // via ?backend=webgpu.
  log.info('createGpuEngine', {
    prefer: opts.prefer,
    hasWebGpu: hasWebGpu(),
    hasWebGl2: hasWebGl2(),
  });
  if (opts.prefer === 'webgpu' && hasWebGpu()) {
    try {
      log.info('requesting WebGPU adapter');
      const adapter = await navigator.gpu!.requestAdapter();
      log.info('WebGPU adapter:', adapter ? 'OK' : 'null');
      if (adapter) {
        const { WebGpuEngine } = await import('./backends/webgpu');
        return new WebGpuEngine();
      }
    } catch (err) {
      log.warn('WebGPU init failed, falling back to WebGL2', err);
    }
  }

  if (hasWebGl2()) {
    log.info('selecting WebGL2 backend');
    const { WebGl2Engine } = await import('./backends/webgl2');
    return new WebGl2Engine();
  }

  if (hasWebGpu()) {
    try {
      log.info('last-ditch WebGPU fallback');
      const adapter = await navigator.gpu!.requestAdapter();
      if (adapter) {
        const { WebGpuEngine } = await import('./backends/webgpu');
        return new WebGpuEngine();
      }
    } catch (err) {
      log.warn('WebGPU fallback init failed', err);
    }
  }

  throw new Error('No GPU backend available (WebGPU and WebGL2 both unsupported)');
}

export function hasWebGpu(): boolean {
  return typeof navigator !== 'undefined' && 'gpu' in navigator && !!navigator.gpu;
}

export function hasWebGl2(): boolean {
  if (typeof document === 'undefined') return false;
  try {
    const c = document.createElement('canvas');
    return !!c.getContext('webgl2');
  } catch {
    return false;
  }
}
